import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Input, Button, notification, Select, Tooltip } from "antd";
import {
  SendOutlined,
  CloseOutlined,
  AudioOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import KaiAvatar from "./KaiAvatar";
import { kaiClient, kaiErrorDescription } from "./kaiClient";
import { useKaiContext } from "./KaiContext";
import { useVoice } from "./useVoice";
import VoiceOrb from "./VoiceOrb";
import ConfirmActionCard from "./ConfirmActionCard";
import { isDraftApplicableWrite } from "./kaiDraftApply";
import { isTemplateDraftApplicableWrite } from "./kaiTemplateApply";
import {
  getProductCompletionStatus,
  getQuickRepliesForField,
  questionForField,
  coachDockHint,
  tryApplyGuidanceAnswer,
  successMessage,
  validationErrorMessage,
} from "./guidanceFieldCoach";
import {
  buildSettingsGuidanceIntro,
  buildPreviewSettingsGuideStatus,
  saveSettingsGuideState,
} from "./settingsCompletionGuide";
import "./kaiSidekick.css";

function coachHelpers(catalogHelpers) {
  return {
    ...catalogHelpers,
    brandNames: catalogHelpers?.brandNames,
    taxClassNames: (catalogHelpers?.taxClasses || []).map((t) => t.name).filter(Boolean),
  };
}

function resolveCoachField(bridge, coachTargetKey, completionStatus) {
  const status = bridge?.getCompletionStatus?.() || completionStatus;
  const missing = status?.missingRequired || [];
  if (coachTargetKey) {
    const targeted = missing.find((f) => f.key === coachTargetKey);
    if (targeted) return { field: targeted, status };
  }
  return { field: missing[0] || null, status };
}

function CoachQuickReplies({ replies, onPick, disabled }) {
  if (!replies?.length) return null;
  return (
    <div className="kai-sk-quick-replies">
      {replies.map((q) => (
        <button
          key={q}
          type="button"
          className="kai-sk-quick-reply"
          disabled={disabled}
          onClick={() => onPick(q)}
        >
          {q}
        </button>
      ))}
    </div>
  );
}

function buildLocalGuidanceIntro(status, appliedFromAi = []) {
  const applied =
    appliedFromAi.length > 0
      ? `I've applied **${appliedFromAi.join(", ")}** from your photo. `
      : "";
  const need = status.missingRequired.map((f) => f.label);
  if (status.isComplete) {
    return `${applied}Your product looks ready to save. Want me to double-check anything or set pricing?`;
  }
  const next = status.missingRequired[0];
  return `${applied}You're **${status.percent}%** there, just **${need.length}** required field${need.length === 1 ? "" : "s"} left${need.length ? `: ${need.join(", ")}` : ""}.

Let's finish together. Type or tap the mic. ${next ? `First up: **${next.label}**.` : ""}`;
}

function ProductGuidanceBanner({ status, onChipClick, onScroll }) {
  if (!status) return null;
  const chips = status.missingRequired.length
    ? status.missingRequired
    : status.missing.slice(0, 3);

  return (
    <div className="kai-sk-guidance">
      <div className="kai-sk-guidance-top">
        <KaiAvatar size={22} className="kai-sk-guidance-icon" />
        <div>
          <div className="kai-sk-guidance-title">Product setup coach</div>
          <div className="kai-sk-guidance-sub">
            {status.isComplete ? "All required fields filled" : `${status.done}/${status.total} fields`}
          </div>
        </div>
        <div className="kai-sk-guidance-ring" style={{ "--pct": status.percent }}>
          <span>{status.percent}%</span>
        </div>
      </div>
      {chips.length > 0 && (
        <div className="kai-sk-guidance-chips">
          {chips.map((f) => (
            <button
              key={f.key}
              type="button"
              className="kai-sk-guidance-chip"
              onClick={() => {
                onScroll?.(f.section);
                onChipClick?.(f);
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingsGuidanceBanner({ status, onGoToSection }) {
  if (!status) return null;
  const chips = [...status.essentialMissing, ...status.recommendedMissing];

  return (
    <div className="kai-sk-guidance">
      <div className="kai-sk-guidance-top">
        <KaiAvatar size={22} className="kai-sk-guidance-icon" />
        <div>
          <div className="kai-sk-guidance-title">Settings guide</div>
          <div className="kai-sk-guidance-sub">
            {status.isEssentialComplete
              ? "Essentials complete"
              : `${status.essentialMissing.length} essential step${
                  status.essentialMissing.length === 1 ? "" : "s"
                } left`}
          </div>
        </div>
        <div className="kai-sk-guidance-ring" style={{ "--pct": status.percent }}>
          <span>{status.percent}%</span>
        </div>
      </div>
      {chips.length > 0 && (
        <div className="kai-sk-guidance-chips">
          {chips.map((section) => (
            <button
              key={section.key}
              type="button"
              className={`kai-sk-guidance-chip${
                section.priority === "essential" ? " kai-sk-guidance-chip--hot" : ""
              }`}
              onClick={() => onGoToSection(section.key)}
            >
              {section.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Docked, context-aware catalog assistant. Two modes:
//  - Text chat: type → reply as text (no voice).
//  - Voice conversation: tap the mic → speak → KAI talks back (no text), with a
//    Siri-style animated orb. Writes stay confirm-gated.
export default function KaiSidekickPanel() {
  const {
    contextProductId,
    screenContext,
    guidanceRequest,
    clearGuidanceRequest,
    settingsGuidanceRequest,
    clearSettingsGuidanceRequest,
    settingsBridge,
    productDraftBridge,
    templateCustomizerBridge,
    draftRevision,
    catalogHelpers,
    productSaveHandler,
  } = useKaiContext();
  const [open, setOpen] = useState(false);
  const [openTick, setOpenTick] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [pendingAction, setPendingAction] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [productGuidanceMode, setProductGuidanceMode] = useState(false);
  const [settingsGuidanceMode, setSettingsGuidanceMode] = useState(false);
  const [settingsPreviewMode, setSettingsPreviewMode] = useState(false);
  const [completionStatus, setCompletionStatus] = useState(null);
  const [settingsCompletionStatus, setSettingsCompletionStatus] = useState(null);
  const [coachTargetKey, setCoachTargetKey] = useState(null);
  const [saveBusy, setSaveBusy] = useState(false);
  const [savePromptDismissed, setSavePromptDismissed] = useState(false);
  const endRef = useRef(null);
  const guidanceHandledRef = useRef(null);
  const settingsGuidanceHandledRef = useRef(null);
  const pendingOpenRef = useRef(null);

  const [voiceMode, setVoiceMode] = useState(false);
  const [voiceYou, setVoiceYou] = useState("");

  const loggedIn =
    !!localStorage.getItem("merchantId") && !!localStorage.getItem("sessionId");

  const activeScreen = screenContext?.screen;

  const exitSettingsGuidance = useCallback(() => {
    setSettingsGuidanceMode(false);
    setSettingsPreviewMode(false);
    setSettingsCompletionStatus(null);
    pendingOpenRef.current = null;
  }, []);

  const exitProductGuidance = useCallback(() => {
    setProductGuidanceMode(false);
    setCompletionStatus(null);
    setCoachTargetKey(null);
    setSavePromptDismissed(false);
    pendingOpenRef.current = null;
  }, []);

  // Leaving a screen should tear down that screen's coach UI - otherwise settings +
  // product banners, docks, and messages stack on top of each other.
  const prevScreenRef = useRef(activeScreen);
  useEffect(() => {
    const prev = prevScreenRef.current;
    prevScreenRef.current = activeScreen;
    if (!prev || prev === activeScreen) return;

    if (activeScreen !== "settings") {
      exitSettingsGuidance();
      if (prev === "settings") {
        setMessages((m) =>
          m.filter((msg) => msg.kind !== "settings-coach" && msg.kind !== "guidance-intro")
        );
      }
    }

    if (prev === "product_edit" && activeScreen !== "product_edit") {
      exitProductGuidance();
      setOpen(false);
      setMessages([]);
      setInput("");
      setPendingAction(null);
    } else if (activeScreen !== "product_edit") {
      exitProductGuidance();
    }

    setPendingAction(null);
  }, [activeScreen, exitSettingsGuidance, exitProductGuidance]);

  const resetProductCoachSession = useCallback(() => {
    exitProductGuidance();
    setOpen(false);
    setMessages([]);
    setInput("");
    setPendingAction(null);
  }, [exitProductGuidance]);

  useEffect(() => {
    const onProductEditDone = () => resetProductCoachSession();
    window.addEventListener("kai:product-edit-done", onProductEditDone);
    return () => window.removeEventListener("kai:product-edit-done", onProductEditDone);
  }, [resetProductCoachSession]);

  // Draft bridge unmounts when leaving Add/Edit Product - close coach and clear chat.
  useEffect(() => {
    if (!productGuidanceMode || productDraftBridge) return;
    resetProductCoachSession();
  }, [productGuidanceMode, productDraftBridge, resetProductCoachSession]);

  // Settings bridge unmounts when leaving Settings (non-preview).
  useEffect(() => {
    if (!settingsGuidanceMode || settingsBridge || settingsPreviewMode) return;
    exitSettingsGuidance();
    setMessages((m) =>
      m.filter((msg) => msg.kind !== "settings-coach" && msg.kind !== "guidance-intro")
    );
  }, [settingsGuidanceMode, settingsBridge, settingsPreviewMode, exitSettingsGuidance]);

  const refreshCompletion = useCallback(() => {
    const status = productDraftBridge?.getCompletionStatus?.();
    if (status) setCompletionStatus(status);
    return status;
  }, [productDraftBridge]);

  const scrollToEnd = useCallback(
    () => endRef.current?.scrollIntoView({ behavior: "smooth" }),
    []
  );

  const productGuidanceRef = useRef(productGuidanceMode);
  const settingsGuidanceRef = useRef(settingsGuidanceMode);
  useEffect(() => {
    productGuidanceRef.current = productGuidanceMode;
  }, [productGuidanceMode]);
  useEffect(() => {
    settingsGuidanceRef.current = settingsGuidanceMode;
  }, [settingsGuidanceMode]);

  useEffect(() => {
    const onOpenPanel = (e) => {
      if (productGuidanceRef.current || settingsGuidanceRef.current) {
        pendingOpenRef.current = null;
      } else {
        pendingOpenRef.current = e.detail || {};
      }
      setOpen(true);
      // Re-fire pending-message handling even when the panel is already open.
      setOpenTick((n) => n + 1);
    };
    window.addEventListener("kai:open-panel", onOpenPanel);
    return () => window.removeEventListener("kai:open-panel", onOpenPanel);
  }, []);

  useEffect(() => {
    scrollToEnd();
  }, [messages, loading, pendingAction, completionStatus, scrollToEnd]);

  useEffect(() => {
    if (!productGuidanceMode || !productDraftBridge) return;
    const status = productDraftBridge.getCompletionStatus?.();
    if (!status) return;
    setCompletionStatus(status);
    if (!status.isComplete) setSavePromptDismissed(false);
    setCoachTargetKey((prev) => {
      const missing = status.missingRequired || [];
      if (prev && missing.some((f) => f.key === prev)) return prev;
      return missing[0]?.key || null;
    });
  }, [draftRevision, productGuidanceMode, productDraftBridge]);

  const sessionStartRef = useRef(null);

  const ensureSession = useCallback(async () => {
    if (sessionId) return sessionId;
    if (!sessionStartRef.current) {
      sessionStartRef.current = kaiClient
        .start()
        .then((data) => {
          setSessionId(data.sessionId);
          // Never wipe an in-progress coach/catalog thread if messages already exist.
          setMessages((m) =>
            m.length === 0 ? [{ role: "assistant", content: data.greeting }] : m
          );
          return data.sessionId;
        })
        .catch((err) => {
          sessionStartRef.current = null;
          throw err;
        });
    }
    return sessionStartRef.current;
  }, [sessionId]);

  const startSessionQuiet = useCallback(async () => {
    if (sessionId) return sessionId;
    if (!sessionStartRef.current) {
      sessionStartRef.current = kaiClient
        .start()
        .then((data) => {
          setSessionId(data.sessionId);
          return data.sessionId;
        })
        .catch((err) => {
          sessionStartRef.current = null;
          throw err;
        });
    }
    return sessionStartRef.current;
  }, [sessionId]);

  useEffect(() => {
    if (open && !sessionId && loggedIn && !productGuidanceMode && !settingsGuidanceMode) {
      ensureSession().catch((err) =>
        notification.error({
          message: "Could not start KAI",
          description: kaiErrorDescription(err),
        })
      );
    }
  }, [open, sessionId, loggedIn, productGuidanceMode, settingsGuidanceMode, ensureSession]);

  const refreshSettingsCompletion = useCallback(async () => {
    if (settingsPreviewMode) {
      const status = buildPreviewSettingsGuideStatus();
      setSettingsCompletionStatus(status);
      return status;
    }
    const status = await settingsBridge?.refresh?.();
    if (status) setSettingsCompletionStatus(status);
    return status;
  }, [settingsBridge, settingsPreviewMode]);

  const handleGoToSettingsSection = useCallback(
    async (sectionKey) => {
      settingsBridge?.scrollToSection?.(sectionKey);
      const status = await refreshSettingsCompletion();
      if (!status) return;
      const section =
        status.sections.find((s) => s.key === sectionKey) || status.nextSection;
      if (section?.prompt) {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: section.prompt,
            kind: "settings-coach",
          },
        ]);
      }
    },
    [settingsBridge, refreshSettingsCompletion]
  );

  const handleSettingsRemindLater = useCallback(() => {
    saveSettingsGuideState({ dismissed: true });
    exitSettingsGuidance();
    setOpen(false);
    setMessages((m) =>
      m.filter((msg) => msg.kind !== "settings-coach" && msg.kind !== "guidance-intro")
    );
  }, [exitSettingsGuidance]);

  // Settings page → open KAI and guide through essential sections first.
  useEffect(() => {
    if (
      !settingsGuidanceRequest?.id ||
      settingsGuidanceHandledRef.current === settingsGuidanceRequest.id
    ) {
      return;
    }
    settingsGuidanceHandledRef.current = settingsGuidanceRequest.id;

    const initialStatus = settingsGuidanceRequest.status || null;
    const isPreview = Boolean(settingsGuidanceRequest.preview);
    setOpen(true);
    setSettingsGuidanceMode(true);
    setSettingsPreviewMode(isPreview);
    exitProductGuidance();
    setSettingsCompletionStatus(initialStatus);
    setMessages([
      {
        role: "assistant",
        content: buildSettingsGuidanceIntro(initialStatus),
        kind: "guidance-intro",
      },
    ]);
    clearSettingsGuidanceRequest();
    refreshSettingsCompletion();
  }, [
    settingsGuidanceRequest,
    clearSettingsGuidanceRequest,
    refreshSettingsCompletion,
    exitProductGuidance,
  ]);

  useEffect(() => {
    if (open && settingsGuidanceMode) {
      refreshSettingsCompletion();
    }
  }, [open, settingsGuidanceMode, refreshSettingsCompletion]);

  const activeCoachField = useMemo(() => {
    const missing = completionStatus?.missingRequired || [];
    if (coachTargetKey) {
      const stillMissing = missing.find((f) => f.key === coachTargetKey);
      if (stillMissing) return stillMissing;
    }
    return missing[0] || null;
  }, [coachTargetKey, completionStatus]);

  const dispatchToKai = useCallback(
    async (text, { viaVoice = false, showUserBubble = true } = {}) => {
      if (!text?.trim() || loading) return;
      if (showUserBubble && !viaVoice) {
        setMessages((m) => [...m, { role: "user", content: text.trim() }]);
      }
      setLoading(true);
      try {
        const sid = await ensureSession();
        const catalogSnapshot = catalogHelpers
          ? {
              brandNames: catalogHelpers.brandNames,
              categoryNames: catalogHelpers.categoryNames,
              attributeNames: catalogHelpers.attributeNames,
              attributeTypeNames: catalogHelpers.attributeTypeNames,
            }
          : undefined;
        const data = await kaiClient.sendMessage(sid, text.trim(), contextProductId, {
          screenContext,
          catalogSnapshot,
        });
        setPendingAction(data.pendingAction || null);
        const assistantText =
          data.message ||
          (data.pendingAction
            ? `I can ${data.pendingAction.summary || "apply that change"}. Confirm below.`
            : "");
        if (viaVoice) {
          if (assistantText) speakRef.current(assistantText);
        } else if (assistantText || data.readActions?.length || data.pendingAction) {
          // Proposals can be prose-free - still show a bubble + confirm card.
          setMessages((m) => [
            ...m,
            {
              role: "assistant",
              content: assistantText,
              actions: data.readActions,
              quickReplies: data.quickReplies || [],
            },
          ]);
        }
        refreshCompletion();
      } catch (e) {
        if (e?.status === 404) {
          sessionStartRef.current = null;
          setSessionId(null);
        }
        const budgetMsg =
          e?.status === 429 && e?.body?.spentUsd != null
            ? `Monthly AI usage limit reached ($${e.body.spentUsd} of $${e.body.limitUsd}). It resets at the start of next month.`
            : e?.status === 429
            ? e?.message || "Too many AI requests. Please wait a moment."
            : null;
        const fallback = budgetMsg || "Sorry, something went wrong. Please try again.";
        if (viaVoice) speakRef.current(fallback);
        else setMessages((m) => [...m, { role: "assistant", content: fallback }]);
      } finally {
        setLoading(false);
      }
    },
    [loading, ensureSession, contextProductId, screenContext, catalogHelpers, refreshCompletion]
  );

  const helpers = useMemo(() => coachHelpers(catalogHelpers), [catalogHelpers]);

  const coachAskNext = useCallback((status) => {
    const field = status?.missingRequired?.[0];
    if (!field) {
      setCoachTargetKey(null);
      setMessages((m) => {
        if (m.some((msg) => msg.kind === "coach-done")) return m;
        return [
          ...m,
          {
            role: "assistant",
            content: "All required fields are filled. You're ready to save!",
            kind: "coach-done",
          },
        ];
      });
      return;
    }
    setCoachTargetKey(field.key);
    const q = questionForField(field);
    if (!q) return;
    setMessages((m) => {
      const last = m[m.length - 1];
      if (last?.kind === "coach-ask" && last.content === q) return m;
      return [...m, { role: "assistant", content: q, kind: "coach-ask" }];
    });
  }, []);

  const handleCoachAnswer = useCallback(
    async (textArg, { showUserBubble = true, viaVoice = false } = {}) => {
      const text = (typeof textArg === "string" ? textArg : input).trim();
      if (!text || loading) return;
      if (typeof textArg !== "string") setInput("");

      if (pendingAction) {
        if (/^(yes|y|ok|okay|confirm|sure)$/i.test(text)) {
          if (showUserBubble && !viaVoice) {
            setMessages((m) => [...m, { role: "user", content: text }]);
          }
          await confirm(pendingAction.id);
          return;
        }
        if (/^(no|cancel|nope|n)$/i.test(text)) {
          if (showUserBubble && !viaVoice) {
            setMessages((m) => [...m, { role: "user", content: text }]);
          }
          await cancel(pendingAction.id);
          return;
        }
      }

      if (showUserBubble && !viaVoice) {
        setMessages((m) => [...m, { role: "user", content: text }]);
      }

      const bridge = productDraftBridge;
      if (productGuidanceMode && bridge?.applyGuidanceField) {
        const { field, status: liveStatus } = resolveCoachField(
          bridge,
          coachTargetKey,
          completionStatus
        );
        if (!field) {
          const alreadyMsg = "That field is already filled ✓";
          if (viaVoice) speakRef.current(alreadyMsg);
          else
            setMessages((m) => [
              ...m,
              { role: "assistant", content: alreadyMsg, kind: "coach-info" },
            ]);
          coachAskNext(liveStatus);
          return;
        }
        const result = await tryApplyGuidanceAnswer(text, field, {
          helpers,
          applyGuidanceField: bridge.applyGuidanceField,
        });
        if (result?.ok) {
          const newStatus = result.status || bridge.getCompletionStatus?.();
          if (newStatus) setCompletionStatus(newStatus);
          setCoachTargetKey(newStatus?.missingRequired?.[0]?.key || null);
          const msg = successMessage({ ...result, status: newStatus });
          if (viaVoice) speakRef.current(msg);
          else
            setMessages((m) => [
              ...m,
              { role: "assistant", content: msg, kind: "coach-applied" },
            ]);
          if (!newStatus?.isComplete) coachAskNext(newStatus);
          return;
        }
        const errMsg =
          validationErrorMessage(result?.reason, field) ||
          (activeCoachField
            ? `Answer the ${activeCoachField.label} question above, or tap a capsule.`
            : "I couldn't apply that. Try again or pick a suggestion.");
        notification.warning({ message: errMsg });
        if (viaVoice) speakRef.current(errMsg);
        else
          setMessages((m) => [
            ...m,
            { role: "assistant", content: errMsg, kind: "coach-error" },
          ]);
        return;
      }

      await dispatchToKai(text, { viaVoice, showUserBubble: false });
    },
    // confirm/cancel are stable enough for this flow
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      input,
      loading,
      pendingAction,
      productGuidanceMode,
      productDraftBridge,
      completionStatus,
      helpers,
      coachAskNext,
      dispatchToKai,
      coachTargetKey,
      activeCoachField,
    ]
  );

  const send = async (textArg, viaVoice = false) => {
    await handleCoachAnswer(textArg, { showUserBubble: !viaVoice, viaVoice });
  };

  const handleGuidanceChip = useCallback(
    (fieldDef) => {
      if (!fieldDef) return;
      const status = productDraftBridge?.getCompletionStatus?.() || completionStatus;
      const stillMissing = status?.missingRequired?.some((f) => f.key === fieldDef.key);
      if (!stillMissing) {
        notification.info({ message: `${fieldDef.label} is already set ✓` });
        return;
      }
      productDraftBridge?.scrollToSection?.(fieldDef.section);
      setCoachTargetKey(fieldDef.key);
    },
    [productDraftBridge, completionStatus]
  );

  const handleScrollToSection = useCallback(
    (section) => {
      productDraftBridge?.scrollToSection?.(section);
    },
    [productDraftBridge]
  );

  const handleSaveYes = useCallback(async () => {
    if (!productSaveHandler) {
      notification.warning({ message: "Save isn't available on this screen." });
      return;
    }
    setSaveBusy(true);
    try {
      await productSaveHandler();
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Product saved ✓", kind: "coach-saved" },
      ]);
    } catch {
      // handleSave shows validation/API errors.
    } finally {
      setSaveBusy(false);
    }
  }, [productSaveHandler]);

  const handleSaveCancel = useCallback(() => {
    setSavePromptDismissed(true);
    setMessages((m) => [
      ...m,
      {
        role: "assistant",
        content: "No problem. Save whenever you're ready using the Save button below.",
        kind: "coach-save-dismissed",
      },
    ]);
  }, []);

  // After AI image apply → open KAI and start product setup coaching.
  useEffect(() => {
    if (!guidanceRequest?.id || guidanceHandledRef.current === guidanceRequest.id) return;
    guidanceHandledRef.current = guidanceRequest.id;

    const bridge = productDraftBridge;
    const draft = bridge?.getEffectiveDraft?.() || bridge?.getDraft?.() || {};
    const status = getProductCompletionStatus(draft);
    const appliedFromAi = guidanceRequest.appliedFromAi || [];

    setOpen(true);
    exitSettingsGuidance();
    setProductGuidanceMode(true);
    setCompletionStatus(status);
    setCoachTargetKey(status.missingRequired[0]?.key || null);
    setSavePromptDismissed(false);

    const intro = buildLocalGuidanceIntro(status, appliedFromAi);

    clearGuidanceRequest();

    (async () => {
      try {
        await startSessionQuiet();
      } catch {
        // Local coach works without API.
      }
      setMessages([{ role: "assistant", content: intro, kind: "guidance-intro" }]);
      coachAskNext(status);
    })();
  }, [
    guidanceRequest,
    productDraftBridge,
    clearGuidanceRequest,
    startSessionQuiet,
    coachAskNext,
    exitSettingsGuidance,
  ]);

  const confirm = async (actionId) => {
    setConfirmBusy(true);
    const action = pendingAction;
    const bridge = productDraftBridge;
    const templateBridge = templateCustomizerBridge;
    const useDraftApply =
      bridge?.applyKaiWrite &&
      action &&
      isDraftApplicableWrite(action.tool) &&
      bridge.applyKaiWrite;
    const useTemplateApply =
      templateBridge?.isActive &&
      action &&
      isTemplateDraftApplicableWrite(action.tool) &&
      templateBridge.applyKaiWrite;

    try {
      if (useDraftApply) {
        const result = await bridge.applyKaiWrite(action);
        setPendingAction(null);
        if (result?.ok) {
          await new Promise((r) => setTimeout(r, 80));
          const status = bridge.getCompletionStatus?.() || result.status || refreshCompletion();
          if (status) setCompletionStatus(status);
          const appliedMsg = "Applied to your draft.";
          if (voiceMode) speakRef.current(appliedMsg);
          else
            setMessages((m) => [
              ...m,
              { role: "assistant", content: appliedMsg, kind: "draft-applied" },
            ]);
          if (productGuidanceMode) {
            coachAskNext(status);
          }
          try {
            await kaiClient.cancel(sessionId, actionId);
          } catch {
            // Best-effort - draft already updated locally.
          }
        } else {
          notification.warning({ message: "Couldn't apply that to the form" });
        }
      } else if (useTemplateApply) {
        const local = await templateBridge.applyKaiWrite(action);
        const data = await kaiClient.confirm(sessionId, actionId);
        setPendingAction(null);
        const msg =
          data.message ||
          (data.success
            ? local?.ok
              ? "Done. Your live preview has been updated."
              : "Done."
            : "That didn't go through.");
        if (voiceMode) speakRef.current(msg);
        else setMessages((m) => [...m, { role: "assistant", content: msg }]);
        if (data.success) {
          window.dispatchEvent(new CustomEvent("kai:catalog-changed"));
        }
      } else {
        const data = await kaiClient.confirm(sessionId, actionId);
        setPendingAction(null);
        const msg = data.message || (data.success ? "Done." : "That didn't go through.");
        if (voiceMode) speakRef.current(msg);
        else setMessages((m) => [...m, { role: "assistant", content: msg }]);
        refreshCompletion();
        if (data.success) {
          window.dispatchEvent(new CustomEvent("kai:catalog-changed"));
          catalogHelpers?.refreshCatalog?.();
        }
      }
    } catch {
      notification.error({ message: "Confirm failed" });
    } finally {
      setConfirmBusy(false);
    }
  };

  const cancel = async (actionId) => {
    try {
      await kaiClient.cancel(sessionId, actionId);
    } finally {
      setPendingAction(null);
      const msg = "Okay, cancelled. Anything else?";
      if (voiceMode) speakRef.current(msg);
      else setMessages((m) => [...m, { role: "assistant", content: msg }]);
    }
  };

  const voice = useVoice({
    onTranscript: (text) => {
      setVoiceYou(text);
      send(text, true);
    },
    onError: (err) => {
      const code = err && err.code;
      const messages = {
        "no-speech": "I didn't hear anything. Tap Talk and speak.",
        "audio-capture": "No microphone found. Check it's connected.",
        "not-allowed": "Microphone access is blocked. Allow it in your browser settings.",
        "service-not-allowed": "Microphone access is blocked. Allow it in your browser settings.",
        network:
          "Speech needs an internet connection (Chrome sends audio to Google). Check your connection and try again.",
        "language-not-supported": "English speech recognition isn't available in this browser.",
      };
      notification.error({
        message: "Voice",
        description:
          messages[code] ||
          (code ? `Speech error: ${code}` : "I couldn't catch that. Please try again."),
      });
    },
  });
  const speakRef = useRef(voice.speak);
  speakRef.current = voice.speak;

  const startTalking = () => {
    setVoiceMode(true);
    setVoiceYou("");
    voice.startRecording();
  };
  const closeVoice = () => {
    voice.stopRecording();
    voice.stopSpeaking();
    setVoiceMode(false);
  };

  useEffect(() => {
    if (!open || !loggedIn || productGuidanceMode || settingsGuidanceMode) return;
    const detail = pendingOpenRef.current;
    if (!detail) return;
    pendingOpenRef.current = null;

    let cancelled = false;

    (async () => {
      try {
        await ensureSession();
        if (cancelled) return;

        if (detail.voice) {
          setVoiceMode(true);
          setVoiceYou("");
          voice.startRecording();
          return;
        }

        if (detail.message) {
          await dispatchToKai(detail.message, { showUserBubble: true });
        }
      } catch (err) {
        if (!cancelled) {
          notification.error({
            message: "Could not start KAI",
            description: kaiErrorDescription(err),
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, openTick, loggedIn, productGuidanceMode, settingsGuidanceMode]);

  const guidanceActive = productGuidanceMode || settingsGuidanceMode;
  const showSettingsGuidance = settingsGuidanceMode && !productGuidanceMode;
  const showProductGuidance = productGuidanceMode && !settingsGuidanceMode;

  const headerTitle = useMemo(() => {
    if (showSettingsGuidance) return "KAI · Settings guide";
    if (showProductGuidance) return "KAI · Product setup";
    return "KAI · Catalog assistant";
  }, [showSettingsGuidance, showProductGuidance]);

  const inputPlaceholder = useMemo(() => {
    if (pendingAction) return "Confirm or cancel the change above…";
    if (showSettingsGuidance) return "Ask KAI about any settings section…";
    if (showProductGuidance) return "Tell KAI the brand, price, type…";
    return "Type a message… Shift+Enter for a new line, or tap the mic to talk";
  }, [pendingAction, showSettingsGuidance, showProductGuidance]);

  const handleChatInputKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const stopVoice = useCallback(() => {
    setVoiceMode(false);
    voice.stopRecording();
    voice.stopSpeaking();
  }, [voice]);

  const handleClosePanel = useCallback(() => {
    setOpen(false);
    stopVoice();
    if (settingsGuidanceMode && !settingsBridge && !settingsPreviewMode) {
      exitSettingsGuidance();
    }
    if (productGuidanceMode && !productDraftBridge) {
      exitProductGuidance();
    }
  }, [
    stopVoice,
    settingsGuidanceMode,
    settingsBridge,
    settingsPreviewMode,
    productGuidanceMode,
    productDraftBridge,
    exitSettingsGuidance,
    exitProductGuidance,
  ]);

  const handleLauncherToggle = useCallback(() => {
    setOpen((wasOpen) => {
      if (wasOpen) stopVoice();
      return !wasOpen;
    });
  }, [stopVoice]);

  const orbState = voice.recording
    ? "listening"
    : voice.transcribing || loading
    ? "thinking"
    : voice.speaking
    ? "speaking"
    : "idle";
  const orbStatus = voice.recording
    ? "Listening… tap Stop when done"
    : voice.transcribing
    ? "Got that…"
    : loading
    ? "Thinking…"
    : voice.speaking
    ? "Speaking…"
    : "Tap to talk";

  if (!loggedIn) return null;

  return (
    <>
      <button
        className={`kai-sk-launcher${guidanceActive && !open ? " kai-sk-launcher--pulse" : ""}${
          open ? " kai-sk-launcher--open" : ""
        }`}
        onClick={handleLauncherToggle}
        aria-label={open ? "Close KAI assistant" : "Open KAI assistant"}
      >
        <KaiAvatar size={50} animated className="kai-sk-launcher-img" />
      </button>

      {open && (
        <div className={`kai-sk-panel${guidanceActive ? " kai-sk-panel--guidance" : ""}`}>
          <div className="kai-sk-header">
            <KaiAvatar size={36} animated className="kai-sk-logo" />
            <Tooltip title={headerTitle} placement="bottom" getPopupContainer={() => document.body}>
              <span className="kai-sk-header-title">{headerTitle}</span>
            </Tooltip>
            <div className="kai-sk-header-actions">
              {settingsPreviewMode && (
                <span className="kai-sk-header-preview">Preview</span>
              )}
              {showProductGuidance && completionStatus?.isComplete && (
                <CheckCircleOutlined className="kai-sk-header-done" title="Required fields complete" />
              )}
              {showSettingsGuidance && settingsCompletionStatus?.isEssentialComplete && (
                <CheckCircleOutlined className="kai-sk-header-done" title="Essential settings complete" />
              )}
              <button
                type="button"
                className="kai-sk-header-close"
                onClick={handleClosePanel}
                aria-label="Close KAI"
              >
                <CloseOutlined />
              </button>
            </div>
          </div>

          {showSettingsGuidance && (
            <SettingsGuidanceBanner
              status={settingsCompletionStatus}
              onGoToSection={handleGoToSettingsSection}
            />
          )}

          {showProductGuidance && (
            <ProductGuidanceBanner
              status={completionStatus}
              onChipClick={handleGuidanceChip}
              onScroll={handleScrollToSection}
            />
          )}

          <div className="kai-sk-messages">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`kai-sk-msg kai-sk-msg-${m.role}${m.kind ? ` kai-sk-msg--${m.kind}` : ""}`}
              >
                {(m.content || m.actions?.length > 0 || m.quickReplies?.length > 0) && (
                <div className="kai-sk-bubble">
                  {m.content &&
                    (m.kind === "guidance-intro" || m.kind === "settings-coach" ? (
                      <div
                        className="kai-sk-guidance-intro"
                        dangerouslySetInnerHTML={{
                          __html: m.content.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>"),
                        }}
                      />
                    ) : (
                      m.content
                    ))}
                  {m.actions?.length > 0 && (
                    <div className="kai-sk-actions">
                      {m.actions.map((a, j) => (
                        <span key={j} className={`kai-sk-chip ${a.success ? "ok" : "warn"}`}>
                          {a.summary || a.tool}
                        </span>
                      ))}
                    </div>
                  )}
                  {m.quickReplies?.length > 0 && (
                    <CoachQuickReplies
                      replies={m.quickReplies}
                      onPick={(q) => handleCoachAnswer(q)}
                      disabled={loading}
                    />
                  )}
                </div>
                )}
              </div>
            ))}
            {pendingAction && !voiceMode && (
              <ConfirmActionCard
                pendingAction={pendingAction}
                busy={confirmBusy}
                onConfirm={confirm}
                onCancel={cancel}
              />
            )}
            {loading && !voiceMode && <div className="kai-sk-typing">KAI is thinking…</div>}
            <div ref={endRef} />
          </div>

          {voiceMode && (
            <div className="kai-voice-overlay">
              <button className="kai-voice-close" onClick={closeVoice} aria-label="Close voice mode">
                <CloseOutlined />
              </button>
              <VoiceOrb state={orbState} />
              <div className="kai-voice-status">{orbStatus}</div>
              {voiceYou && <div className="kai-voice-you">"{voiceYou}"</div>}

              {pendingAction && (
                <div className="kai-voice-confirm">
                  <ConfirmActionCard
                    pendingAction={pendingAction}
                    busy={confirmBusy}
                    onConfirm={confirm}
                    onCancel={cancel}
                  />
                </div>
              )}

              <button
                type="button"
                className={`kai-voice-talk${voice.recording ? " is-live" : ""}`}
                onClick={() => (voice.recording ? voice.stopRecording() : startTalking())}
                disabled={(loading || voice.transcribing) && !voice.recording}
              >
                <AudioOutlined /> {voice.recording ? "Stop" : "Talk"}
              </button>

              {voice.ttsSupported && voice.voices.length > 0 && (
                <Select
                  size="small"
                  value={voice.voiceName}
                  onChange={voice.selectVoice}
                  className="kai-voice-select"
                  dropdownStyle={{ zIndex: 1300 }}
                  showSearch
                  optionFilterProp="label"
                  options={voice.voices.map((v) => ({
                    value: v.name,
                    label: v.label,
                  }))}
                />
              )}
            </div>
          )}

          {showSettingsGuidance &&
            settingsCompletionStatus?.nextSection &&
            !settingsCompletionStatus.isComplete && (
              <div className="kai-sk-coach-dock">
                <div
                  className="kai-sk-coach-dock-label"
                  dangerouslySetInnerHTML={{
                    __html: settingsCompletionStatus.nextSection.prompt.replace(
                      /\*\*(.*?)\*\*/g,
                      "<strong>$1</strong>"
                    ),
                  }}
                />
                <div className="kai-sk-save-actions">
                  <Button onClick={handleSettingsRemindLater}>Remind me later</Button>
                  <Button
                    type="primary"
                    onClick={() =>
                      handleGoToSettingsSection(settingsCompletionStatus.nextSection.key)
                    }
                  >
                    Go to {settingsCompletionStatus.nextSection.label}
                  </Button>
                </div>
              </div>
            )}

          {showProductGuidance &&
            !pendingAction &&
            !loading &&
            completionStatus?.isComplete &&
            !savePromptDismissed && (
              <div className="kai-sk-coach-dock kai-sk-save-prompt">
                <div className="kai-sk-coach-dock-label">
                  All required fields are filled. Ready to save your product?
                </div>
                <div className="kai-sk-save-actions">
                  <Button onClick={handleSaveCancel} disabled={saveBusy}>
                    Cancel
                  </Button>
                  <Button type="primary" onClick={handleSaveYes} loading={saveBusy}>
                    Yes, save
                  </Button>
                </div>
              </div>
            )}

          {showProductGuidance &&
            !pendingAction &&
            !loading &&
            !completionStatus?.isComplete &&
            activeCoachField && (() => {
              const currentField = activeCoachField;
              const dockReplies = getQuickRepliesForField(currentField, helpers);
              const hint = coachDockHint(currentField, helpers);
              return (
                <div className="kai-sk-coach-dock">
                  <div className="kai-sk-coach-dock-label">
                    {questionForField(currentField)}
                  </div>
                  {dockReplies.length > 0 && (
                    <CoachQuickReplies
                      replies={dockReplies}
                      onPick={(q) => handleCoachAnswer(q)}
                    />
                  )}
                  {hint && <div className="kai-sk-coach-dock-hint">{hint}</div>}
                </div>
              );
            })()}

          <form
            className="kai-sk-input"
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            <Tooltip
              title={inputPlaceholder}
              placement="topLeft"
              mouseEnterDelay={0.35}
              getPopupContainer={() => document.body}
              overlayStyle={{ maxWidth: 300 }}
            >
              <span className="kai-sk-input-field">
                <Input.TextArea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={inputPlaceholder}
                  disabled={loading}
                  onKeyDown={handleChatInputKeyDown}
                  autoSize={{ minRows: 1, maxRows: 6 }}
                  rows={1}
                />
              </span>
            </Tooltip>
            {voice.sttSupported && (
              <Button
                type="default"
                icon={<AudioOutlined />}
                onClick={startTalking}
                disabled={loading}
                title="Talk to KAI"
                aria-label="Talk to KAI"
              />
            )}
            <Button
              type="primary"
              htmlType="submit"
              icon={<SendOutlined />}
              disabled={loading || !input.trim()}
              loading={loading}
            />
          </form>
        </div>
      )}
    </>
  );
}
